<template>
  <div class='headline'>{{ tp('headline') }}</div>
  <div
    ref='swiperRef'
    :class='isMobile ? "swiper" : ""'
  >
    <div
      :class='isMobile ? "swiper-wrapper" : "wrapper"'
    >
      <div
        v-for='size in sizes'
        :key='size'
        :class='isMobile ? "swiper-slide" : "slide"'
      >
        <!--
          generate images from originals:
          local image_path=app/packs/images/vue/storage_schema_boxes/guide_modal; cp $image_path/original/* $image_path/; for image in $image_path/*@2x.png; do; echo $image; convert -resize 50% $image ${image/@2x/}; convert -quality 95% $image ${image/\.png/.webp}; convert -resize 50% ${image/\.png/.webp} ${${image/\.png/.webp}/@2x/}; tinypng $image; tinypng ${image/@2x/}; done;
        -->
        <picture>
          <source
            :srcset='getSrcUrl(size, ".webp") + ", " + getSrcUrl(size, "@2x.webp") + " 2x"'
            type='image/webp'
          >
          <img
            :src='getSrcUrl(size, ".png")'
            :srcset='getSrcUrl(size, "@2x.png") + " 2x"'
            loading='lazy'
          >
        </picture>
        <div class='size-button'>
          <div class='size'>
            <div class='tag'>{{ tpIndex(`sizes.${size}.title`) }}</div>
            <span>{{ tpIndex(`sizes.${size}.subtitle`) }}</span>
          </div>
          <button
            v-if='!isMobile'
            :disabled='!availableSizes.includes(size)'
            @click='emit("size:select", size)'
          >
            {{ tp(`size.${size}.button`) }}
          </button>
        </div>
        <div class='text'>{{ tp(`size.${size}.text`) }}</div>
        <div class='what-fits'>{{ tp('what_fits') }}</div>
        <ul>
          <li
            v-for='(text, type) in tp(`size.${size}.what_fits`)'
            :key='type'
            :class='{ [type]: true }'
          >
            {{ text }}
          </li>
        </ul>
      </div>
    </div>
  </div>
  <div v-if='isMobile' class='button-container'>
    <button
      :disabled='!availableSizes.includes(activeSize)'
      @click='emit("size:select", activeSize)'
    >
      {{ tp(`size.${activeSize}.button`) }}
    </button>
  </div>
</template>

<script>
export default {
  name: 'GuideModal'
};
</script>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';

import { t, prepared } from '@/helpers/i18n';

import { useMobileDetect } from '../composables/mobile_detect';

const props = defineProps({
  sizes: { type: Array, required: true },
  availableSizes: { type: Array, required: true }
});
const emit = defineEmits(['size:select', 'modal:close']);
const tp = prepared('frontend.storage_schema_boxes.guide_modal');
const tpIndex = prepared('frontend.storage_schema_boxes.index');

const { isMobile } = useMobileDetect();

const swiperRef = ref(null);
const swiper = ref(null);
const activeSize = ref(props.sizes[0]);

function switchSlide(size) {
  swiper.value.slideTo(props.sizes.indexOf(size));
}

function getSrcUrl(size, suffix) {
  return require(
    '../../../images/vue/storage_schema_boxes/guide_modal/' +
      size +
      '-' +
      (isMobile.value ? 'mobile' : 'desktop') +
      suffix
  );
}

onMounted(async () => {
  if (props.sizes.length < 2) { return; }
  if (!isMobile.value) { return; }

  const [{ default: Swiper }, { EffectFade, Pagination }] = await Promise.all([
    import('swiper'),
    import('swiper/modules')
  ]);

  swiper.value = new Swiper(swiperRef.value, {
    wrapperClass: 'swiper-wrapper',
    slideClass: 'swiper-slide',
    slidesPerView: 'auto',
    on: {
      slideChange() {
        activeSize.value = props.sizes[this.realIndex];
      }
    }

  });
});

onBeforeUnmount(() => {
  if (swiper.value) {
    swiper.value.destroy();
    swiper.value = null;
  }
});
</script>

<style scoped lang='sass'>
@import 'app/packs/stylesheets/functions/rem'
@import 'app/packs/stylesheets/mixins/button_v4'
@import 'app/packs/stylesheets/mixins/responsive'
@import 'app/packs/stylesheets/mixins/typography'

$image_path: '../../../images/vue/storage_schema_boxes/guide_modal'

.headline
  user-select: none

  +lte_ipad
    +p1(700)
    margin-bottom: rem4(24px)
    padding: 0 var(--modal-padding)

  +gte_laptop
    +h4
    letter-spacing: -0.48px
    margin-bottom: 24px

+lte_ipad
  .swiper
    --image-width: #{rem4(310px)}
    position: relative
    display: flex

    .swiper-slide
      display: flex
      flex-direction: column
      gap: rem4(16px)
      height: auto
      transition: opacity var(--transition-duration)

      +lte_ipad
        padding-left: var(--modal-padding)
        width: calc(var(--image-width) + var(--modal-padding))

      &.swiper-slide-next
        +iphone
          opacity: 0.4

        +gte_laptop
          opacity: 0.4

      &:not(:last-child)
        margin-right: calc(#{rem4(36px)} - var(--modal-padding))

      &:last-child
        padding-right: var(--modal-padding)
        width: calc(rem4(310px) + var(--modal-padding) * 2)

    picture img
      width: var(--image-width)

+gte_laptop
  .wrapper
    display: flex
    flex-direction: column
    gap: 48px

  .slide
    display: flex
    flex-direction: column
    gap: 16px

.size-button
  display: flex
  justify-content: space-between

  +lte_ipad
    margin-bottom: rem4(8px)

  +gte_laptop
    order: -1

  .size
    align-items: center
    display: flex

    +lte_ipad
      gap: rem4(16px)

    +gte_laptop
      gap: 16px

    .tag
      +p3
      align-items: center
      background: var(--gray-100)
      border-radius: 100px
      display: flex
      flex-direction: column
      justify-content: center
      letter-spacing: 0.16px

      +lte_ipad
        padding: rem4(4px) rem4(12px)

      +gte_laptop
        padding: 4px 12px

    span
      +p3(700)
      letter-spacing: 0.16px

  button
    +button_v4('primary', 'sm')

.text
  +p4

.what-fits
  +p4(700)

ul
  display: flex
  flex-wrap: wrap

  +lte_ipad
    gap: rem4(12px)

  +gte_laptop
    gap: 12px

  li
    +p4
    align-items: center
    background: var(--gray-100)
    border-radius: 100px
    display: flex
    justify-content: center

    +lte_ipad
      gap: rem4(8px)
      padding: rem4(4px) rem4(8px)

    +gte_laptop
      gap: 8px
      padding: 4px 8px

    &:before
      align-items: center
      background-position: center
      background-repeat: no-repeat
      background-size: cover
      display: flex
      flex-shrink: 0
      justify-content: center

      +lte_ipad
        height: rem4(20px)
        width: rem4(20px)

      +gte_laptop
        height: 20px
        width: 20px

    // also copied to boxes-v4/show.sass
    @each $type in bicycle, broom, cabinet, clothes, construction, furniture, goods, skates, sofa, tent, tree, work, medal, documents, suitcase
      &.#{$type}:before
        content: ''
        background-image: url('#{$image_path}/what_fits/#{$type}.svg')

    // из figma не экспортируется нормально svg, поэтому сделал png
    &.chair:before
      content: ''
      background-image: url('#{$image_path}/what_fits/chair@2x.png')

+lte_ipad
  .swiper
    padding-bottom: rem4(84px)

  .button-container
    background: #fff
    border-radius: rem4(16px) rem4(16px) 0 0
    // border-radius: rem4(16px)
    bottom: 0
    box-shadow: 0px 0px 24.944px 0px rgba(3, 3, 3, 0.10)
    left: 0
    padding: rem4(24px)
    position: fixed
    width: 100%
    z-index: 1

    button
      +button_v4('primary', 'xl')
      width: 100%
</style>
